import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { CreateLineItem, UpdateLineItem } from '@trellis/shared'

import { usePagedInfiniteQuery } from '@/hooks/usePagedInfiniteQuery'
import {
  assignTagsToLineItem,
  createLineItem,
  deleteLineItem,
  getLineItem,
  listLineItems,
  removeTagFromLineItem,
  suggestLineItems,
  updateLineItem,
  type LineItemsListParams,
} from '@/requests/line-items'

/** Fetches all line items (up to 500) for total count in the page header. */
export function useLineItemsSummaryQuery() {
  return useQuery({
    queryKey: ['line-items', 'summary'],
    queryFn: () => listLineItems({ limit: 100 }),
    staleTime: 60_000,
  })
}

export function useLineItemsQuery(options: LineItemsListParams = {}) {
  const limit = options.limit ?? 20

  const query = useInfiniteQuery({
    queryKey: ['line-items', options],
    queryFn: ({ pageParam }) =>
      listLineItems({ ...options, cursor: pageParam ?? options.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
  })

  return { ...query, ...usePagedInfiniteQuery(query, limit) }
}

export function useLineItemQuery(id: string) {
  return useQuery({
    queryKey: ['line-items', id],
    queryFn: () => getLineItem(id),
    enabled: !!id,
  })
}

export function useCreateLineItemMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateLineItem) => {
      const result = await createLineItem(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['line-items'] })
    },
  })
}

export function useUpdateLineItemMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateLineItem }) => {
      const result = await updateLineItem(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['line-items'] })
      await queryClient.invalidateQueries({ queryKey: ['line-items', variables.id] })
    },
  })
}

export function useDeleteLineItemMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteLineItem(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['line-items'] })
    },
  })
}

export function useAssignLineItemTagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lineItemId, tagIds }: { lineItemId: string; tagIds: string[] }) =>
      assignTagsToLineItem(lineItemId, tagIds),
    onSuccess: async (_, { lineItemId }) => {
      await queryClient.invalidateQueries({ queryKey: ['line-items', lineItemId] })
    },
  })
}

export function useLineItemSuggestionsQuery(q: string) {
  return useQuery({
    queryKey: ['line-items', 'suggest', q],
    queryFn: () => suggestLineItems(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  })
}

export function useRemoveLineItemTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lineItemId, tagId }: { lineItemId: string; tagId: string }) =>
      removeTagFromLineItem(lineItemId, tagId),
    onSuccess: async (_, { lineItemId }) => {
      await queryClient.invalidateQueries({ queryKey: ['line-items', lineItemId] })
    },
  })
}
